import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, CircularProgress, Stack} from '@mui/material';
import {useLocation, useNavigate} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import SourcesFileViewerModal from '../../products/reportEditorPage/leftPanel/components/SourcesFileViewerModal';
import {getCollectionMaterial} from '../collectionTaskApi';
import {CM_TAG_ROLES, hasAnyRole} from '../collectionTaskPayload';
import CollectionMaterialHeader from './CollectionMaterialHeader';
import IirMaterialViewer from './IirMaterialViewer';
import {
    CM_MATERIAL_KIND_CM,
    CM_MATERIAL_KIND_IIR,
    readCollectionMaterialViewerState,
    stripCollectionMaterialViewerParams,
} from './openCollectionMaterial';

/**
 * Globally mounted host for the Collected Material viewer (contract C2).
 * URL-driven: opens the shared Sources viewer modal whenever cmView* query
 * params are present on the current URL — from any page — with the gray CM
 * header, tags, and a "<" back button restoring the exact prior URL.
 *
 * materialKind 'iir' renders the IIR flavor (P10): same modal shell composed
 * with the gray IIR header and P11's IirBodyRenderer — see IirMaterialViewer.
 */
export default function CollectionMaterialViewerHost() {
    const location = useLocation();
    const navigate = useNavigate();
    const {role, roles} = useSession() || {};

    const viewerState = useMemo(
        () => readCollectionMaterialViewerState(location.search),
        [location.search],
    );
    const materialId = viewerState?.materialKind === CM_MATERIAL_KIND_CM
        ? viewerState.materialId
        : null;
    const iirReportId = viewerState?.materialKind === CM_MATERIAL_KIND_IIR
        ? viewerState.materialId
        : null;

    const [material, setMaterial] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setMaterial(null);
        setError(null);
        if (!materialId) return undefined;
        getCollectionMaterial(materialId)
            .then((m) => {
                if (cancelled) return;
                if (!m) {
                    setError('Collected Material not found');
                    return;
                }
                setMaterial(m);
            })
            .catch((e) => {
                if (!cancelled) setError(e?.message || 'Failed to load material');
            });
        return () => {
            cancelled = true;
        };
    }, [materialId]);

    const handleClose = useCallback(() => {
        navigate({
            pathname: location.pathname,
            search: stripCollectionMaterialViewerParams(location.search),
            hash: location.hash,
        }, {replace: true});
    }, [navigate, location.pathname, location.search, location.hash]);

    const handleBack = useCallback(() => {
        if (viewerState?.returnTo) {
            navigate(viewerState.returnTo);
            return;
        }
        handleClose();
    }, [navigate, viewerState?.returnTo, handleClose]);

    if (iirReportId) {
        return (
            <IirMaterialViewer
                reportId={iirReportId}
                onClose={handleClose}
                onBack={handleBack}
            />
        );
    }

    if (!materialId) {
        return null;
    }

    let contentSlot = null;
    if (error) {
        contentSlot = <Alert severity="error">{error}</Alert>;
    } else if (!material) {
        contentSlot = (
            <Stack flex={1} alignItems="center" justifyContent="center">
                <CircularProgress size={28}/>
            </Stack>
        );
    } else if (!material.dataItemId) {
        contentSlot = (
            <Alert severity="info">
                This material has no linked content yet. Its metadata and tags are shown above.
            </Alert>
        );
    }

    const dataItemId = material?.dataItemId != null ? String(material.dataItemId) : null;
    const canEditTags = hasAnyRole(role, roles, CM_TAG_ROLES);

    return (
        <SourcesFileViewerModal
            controlled={{
                isOpen: true,
                fileId: dataItemId,
                canonicalFileId: dataItemId,
                fileName: material?.title || material?.cmNumber || 'Collected Material',
                fileType: material?.mimeType || 'unknown',
                fileSource: 'collection_material',
                onClose: handleClose,
            }}
            headerSlot={material
                ? (
                    <CollectionMaterialHeader
                        material={material}
                        canEditTags={canEditTags}
                        onDeleted={handleBack}
                        onMaterialUpdated={setMaterial}
                    />
                )
                : null}
            contentSlot={contentSlot}
            onBack={handleBack}
            hideContentToolbar
        />
    );
}
